import React, { useEffect, useState } from 'react';
import { Alert, Box, Typography, CircularProgress, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  useCustomTranscriberCreateControllerCreateTranscriberMutation,
  useCustomTranscriberEditControllerEditTranscriberMutation,
  useTranscriberListControllerAdminTranscriberListQuery,
} from '@shared/services/apiService/apiService';
import type {
  CustomTranscriberRequestDto,
  TranscriberResponseDto,
} from '@shared/services/apiService/apiService';
import { TranscriberTable } from '../components/TranscriberTable';
import { useTranscriberContext } from '../TranscriberContext';
import { TranscriberCreateDialog } from '../components/TranscriberCreateDialog';
import { TranscriberDeleteDialog } from '../components/TranscriberDeleteDialog';
import { CustomTranscriberDialog } from '../components/CustomTranscriberDialog';

export const ManageTranscriberPageContent: React.FC = () => {
  const { openDialog, refreshKey } = useTranscriberContext();
  const { t } = useTranslation();
  const {
    isLoading: isLoadingTranscribers,
    isFetching: isFetchingTranscribers,
    isError: isTranscriberListError,
    refetch: refreshTranscribers,
    data: transcribers = [],
  } = useTranscriberListControllerAdminTranscriberListQuery();
  const [createCustomTranscriber] = useCustomTranscriberCreateControllerCreateTranscriberMutation();
  const [editCustomTranscriber] = useCustomTranscriberEditControllerEditTranscriberMutation();
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [selectedCustomTranscriber, setSelectedCustomTranscriber] = useState<
    TranscriberResponseDto | undefined
  >();

  useEffect(() => {
    refreshTranscribers();
  }, [refreshKey]);

  const handleAddTranscriberClick = () => {
    openDialog('createOpen');
  };

  const handleSaveCustomTranscriber = async (
    customTranscriber: CustomTranscriberRequestDto,
    id?: string,
  ): Promise<void> => {
    if (id) {
      await editCustomTranscriber({
        id,
        customTranscriberRequestDto: customTranscriber,
      }).unwrap();
    } else {
      await createCustomTranscriber({ customTranscriberRequestDto: customTranscriber }).unwrap();
    }
    toast.success(t(id ? 'transcriberPage.editSuccess' : 'transcriberPage.createSuccess'), {
      closeButton: false,
      autoClose: 5000,
      icon: false,
    });
    refreshTranscribers();
  };

  const handleEditCustomTranscriber = (customTranscriber: TranscriberResponseDto) => {
    setSelectedCustomTranscriber(customTranscriber);
    setCustomDialogOpen(true);
  };

  const handleCloseCustomDialog = () => {
    setCustomDialogOpen(false);
    setSelectedCustomTranscriber(undefined);
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 2, alignItems: 'center' }}>
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              setSelectedCustomTranscriber(undefined);
              setCustomDialogOpen(true);
            }}
            sx={{ width: { xs: '100%', sm: 'auto' } }}>
            {t('transcriberPage.actions.addCustomTranscriber')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddTranscriberClick}
            sx={{ width: { xs: '100%', sm: 'auto' } }}>
            {t('transcriberPage.actions.addTranscriber')}
          </Button>
        </Stack>
      </Box>

      {isLoadingTranscribers && transcribers.length === 0 && (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      )}

      {isTranscriberListError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('transcriberPage.custom.loadFailed')}
        </Alert>
      )}

      {isFetchingTranscribers && transcribers.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <CircularProgress size={20} aria-label={t('transcriberPage.custom.loading')} />
        </Box>
      )}

      {!isLoadingTranscribers && !isTranscriberListError && transcribers.length === 0 && (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Typography variant="h6">{t('superAdminManageVoice.noData')}</Typography>
        </Box>
      )}

      {transcribers.length > 0 && (
        <TranscriberTable transcribers={transcribers} onEditCustom={handleEditCustomTranscriber} />
      )}

      <TranscriberCreateDialog />

      <TranscriberDeleteDialog />

      {customDialogOpen && (
        <CustomTranscriberDialog
          key={selectedCustomTranscriber?.id ?? 'new'}
          open
          transcriber={selectedCustomTranscriber}
          onClose={handleCloseCustomDialog}
          onSave={handleSaveCustomTranscriber}
        />
      )}
    </>
  );
};
